import { ValidationError } from "sequelize"
import { useSelector } from "react-redux"
import {
    MtProject,
    MtProjectCreate,
    MtProjectSave,
    MtDriveFolderCreate,
} from "server/models"
import { VALUE, UNLIMITED, VIEW } from "const"
import { deepCopy, hasAccess, OrderType } from "client/utils"
import {
    GET, POST, DELETE, sort, search, validate,
} from "client/store/utils"
import {
    PRM_PROJECT,
    StoreType,
    LangType,
    ProjectValidation,
    PackageSettings,
    AccountSettings,
    ProjectSettings,

    SaveProjectSettings,
    SaveProjectUserList,
    CopyDefaultProjectGroups,
    CreateDriveFolder,

    initProjectSettings,
} from "client/store"

import ImportDefaultProjectSettings from "client/pages/settings/importProjectSettings"

import {
    ProjectList,
    ProjectFilters,
} from "./types"


export const LoadAllProjects = (accountId: number) => (
    GET(`/mtProject/accountId/${accountId}`)
    .then((projects: MtProject[]) => {
        const projectList: ProjectList = {}
        projects.forEach((project) => {
            projectList[project.id] = project
        })
        return projectList
    })
)

export const LoadProjectById = (
    projectId: number,
    accountId: number,
) => (
    GET(`/mtProject/id/${projectId}/accountId/${accountId}`)
    .then((project: MtProject) => project)
)

export const LoadProjectByIdOnly = ( // for Super Admin
    projectId: number,
) => (
    GET(`/mtProject/idOnly/${projectId}`)
    .then((project: MtProject) => project)
)

export const SaveProject = (
    id: number,
    project: MtProjectCreate,
    participants: readonly number[],
    maxProjects: number,
    accountSettings: AccountSettings,
    lang: LangType,
) => (
    LoadAllProjects(project.accountId).then((projectList) => {
        if (!id && maxProjects !== null && Object.keys(projectList).length >= maxProjects) {
            return {
                newProjectId: null,
                validation: <ProjectValidation>{ publicId: lang.maxProjectsReached.replace("{0}", maxProjects.toString()) },
            }
        }

        const data: MtProjectSave = {
            id,
            project,
        }
        return POST(
            "/mtProject/save",
            JSON.stringify(data),
        ).then((res) => {
            const newProject = res as MtProject
            const error = res as ValidationError

            const newProjectId = newProject.id || null
            const validation = <ProjectValidation>validate(error, lang)

            if (validation) {
                return {
                    newProjectId,
                    validation,
                }
            }

            const promiseArray: Promise<any>[] = [
                SaveProjectUserList(participants, newProjectId || id),
            ]
            if (newProjectId) {
                const newFolder: MtDriveFolderCreate = {
                    folderId: 0,
                    prevFolderId: null,
                    accountId: project.accountId,
                    projectId: newProjectId,
                    qidProductId: null,
                    name: "Project folder",
                    copy: 0,
                }

                const newSettings = deepCopy(initProjectSettings) as ProjectSettings
                newSettings.sil.assumption = accountSettings.sil.defaultAssumption

                promiseArray.push(
                    CreateDriveFolder(newFolder, lang),
                    CopyDefaultProjectGroups(project.accountId, newProjectId, accountSettings.project.defaultGroupsAssigned)
                    .then(() => (
                        SaveProjectSettings(newProjectId, newSettings).then(() => (
                            ImportDefaultProjectSettings(newProject)
                        ))
                    )),
                )
            }
            return Promise.all(promiseArray).then(() => ({
                newProjectId,
                validation,
            }))
        })
    })
)

export const DeleteProject = (
    projectId: number,
    accountId: number,
) => (
    DELETE(`/mtProject/delete/${projectId}/${accountId}`)
    .then((removedRows: number) => removedRows)
)

export const SortProjects = (
    projects: MtProject[],
    sortColumn: keyof MtProject,
    sortOrder: OrderType,
    filters: ProjectFilters,
) => {
    const projectsFiltered = projects.filter((project) => {
        let showRow = true
        if (filters.owner && project.owner !== filters.owner) showRow = false
        else if (filters.search) showRow = search(filters.search, project)
        return showRow
    })
    projectsFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))

    const projectIds: number[] = []
    projectsFiltered.forEach((project) => projectIds.push(project.id))

    return projectIds
}

export const GetMaxProjects = (packageSettings: PackageSettings) => {
    const { projects, projectsValue } = packageSettings
    let maxProjects = 0
    if (projects === 1) maxProjects = 1
    if (projects === VALUE && projectsValue) maxProjects = projectsValue
    if (projects === UNLIMITED) maxProjects = null

    return maxProjects
}

export const GetProjectViewAccess = (projectId: number) => {
    const userData = useSelector((state: StoreType) => state.system.userData)
    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)

    const viewAccess = hasAccess(PRM_PROJECT, VIEW)
    const participants = (projectUserList && projectUserList[projectId]) || []

    return viewAccess || participants.includes(userData.userId)
}
